import React, { useEffect, useState } from "react";

const SLUGS = ["privacy", "terms", "cookies", "transparency", "donation-terms", "whistleblower"];

/**
 * LegalAdmin — edits the flat `legal_page` collection.
 * Contract: GET /admin/legal, GET/PUT /admin/legal/{slug}, POST /admin/legal/seed-missing.
 */
export default function LegalAdmin({ authed }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => authed.get("/admin/legal").then(r => setRows(r.data || []));
  useEffect(() => { load(); }, []);

  const seed = async () => {
    const r = await authed.post("/admin/legal/seed-missing", {});
    setMsg(`Seeded ${r.data.inserted} missing legal pages.`);
    load();
  };

  return (
    <div data-testid="legal-admin" className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="font-editorial text-2xl">Legal pages</h2>
          <p className="text-ink/60 text-sm">Slug-based rich-text pages: privacy, terms, cookies, transparency, donation-terms, whistleblower.</p>
        </div>
        <button onClick={seed} data-testid="legal-seed"
                className="text-xs border border-line px-3 py-1.5">Seed missing</button>
      </header>
      {msg && <div className="text-sm text-terracotta">{msg}</div>}

      <div className="border border-line rounded">
        <table className="w-full text-sm">
          <thead className="bg-line/40">
            <tr>{["slug", "title (EN)", "updated", "state", ""].map(c =>
              <th key={c} className="text-left p-3 label-mono">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const d = r.data || {};
              return (
                <tr key={d.slug} className="border-t border-line">
                  <td className="p-3 font-mono text-xs">{d.slug}</td>
                  <td className="p-3">{d.title?.en}</td>
                  <td className="p-3">{d.updated}</td>
                  <td className="p-3">
                    <span className={`label-mono text-[10px] px-2 py-1 rounded ${d.state === "live" ? "bg-terracotta/10 text-terracotta" : "bg-ink/10 text-ink/70"}`}>{d.state || "draft"}</span>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => setEditing(d)}
                            data-testid={`legal-edit-${d.slug}`}
                            className="label-mono text-terracotta">Edit</button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-ink/50">No legal pages — click <em>Seed missing</em>.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <LegalEditor page={editing} authed={authed}
                               onClose={() => setEditing(null)}
                               onSaved={() => { setEditing(null); load(); setMsg("Saved."); }} />}
    </div>
  );
}

function LegalEditor({ page, authed, onClose, onSaved }) {
  const [d, setD] = useState({ ...page });
  const setTx = (k, l, v) => setD(x => ({ ...x, [k]: { ...(x[k] || {}), [l]: v } }));
  const save = async () => {
    await authed.put(`/admin/legal/${d.slug}`, {
      slug: d.slug,
      title: d.title || { en: "", da: "", es: "" },
      body:  d.body  || { en: "", da: "", es: "" },
      updated: d.updated || "",
      state: d.state || "live",
      seo: d.seo || { title: d.title, description: {} },
    });
    onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded p-6"
           onClick={e => e.stopPropagation()}
           data-testid={`legal-editor-${d.slug}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-editorial text-2xl">{d.slug}</h3>
          <button onClick={onClose} className="label-mono text-ink/50">✕</button>
        </div>

        <div className="space-y-4">
          <TxField label="Title" k="title" d={d} setTx={setTx} />
          <TxField label="Body (supports **bold** and blank lines)" k="body" d={d} setTx={setTx} multiline />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="label-mono text-ink/60 text-[10px] mb-1">Updated</div>
              <input value={d.updated || ""} onChange={e => setD({ ...d, updated: e.target.value })}
                     className="w-full border border-line px-2 py-1.5 text-sm"
                     data-testid={`legal-updated-input-${d.slug}`} />
            </div>
            <div>
              <div className="label-mono text-ink/60 text-[10px] mb-1">State</div>
              <select value={d.state || "live"} onChange={e => setD({ ...d, state: e.target.value })}
                      className="w-full border border-line px-2 py-1.5 text-sm"
                      data-testid={`legal-state-input-${d.slug}`}>
                <option value="draft">Draft</option>
                <option value="ready">Ready</option>
                <option value="live">Live</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={save} data-testid={`legal-save-${d.slug}`}
                  className="bg-terracotta text-white px-6 py-2 text-sm">Save</button>
          <button onClick={onClose} className="border border-line px-6 py-2 text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function TxField({ label, k, d, setTx, multiline }) {
  return (
    <div>
      <div className="label-mono text-ink/60 text-[10px] mb-1">{label}</div>
      {["en", "da", "es"].map(l => (
        <div key={l} className="mb-2">
          <div className="label-mono text-ink/50 text-[9px]">{l.toUpperCase()}</div>
          {multiline ? (
            <textarea rows={12} value={(d[k] && d[k][l]) || ""}
                      onChange={e => setTx(k, l, e.target.value)}
                      className="w-full border border-line px-2 py-1.5 text-sm mt-1 font-mono"
                      data-testid={`legal-${k}-${l}`} />
          ) : (
            <input value={(d[k] && d[k][l]) || ""}
                   onChange={e => setTx(k, l, e.target.value)}
                   className="w-full border border-line px-2 py-1.5 text-sm mt-1"
                   data-testid={`legal-${k}-${l}`} />
          )}
        </div>
      ))}
    </div>
  );
}
